/**
 * Frontend Cache Service
 * Manages API response caching with TTL support and automatic cleanup
 */

class CacheEntry {
  constructor(data, ttl) {
    this.data = data;
    this.timestamp = Date.now();
    this.ttl = ttl;
  }

  isExpired() {
    return Date.now() - this.timestamp > this.ttl;
  }
}

class CacheService {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map(); // Deduplicate concurrent requests
    this.startCleanup();
  }

  startCleanup() {
    this.cleanupInterval = setInterval(() => {
      for (const [key, entry] of this.cache.entries()) {
        if (entry.isExpired()) {
          this.cache.delete(key);
        }
      }
    }, 5 * 60 * 1000); // Every 5 minutes
  }

  set(key, data, ttl = 5 * 60 * 1000) {
    this.cache.set(key, new CacheEntry(data, ttl));
  }

  get(key) {
    const entry = this.cache.get(key);
    if (!entry) return null;
    if (entry.isExpired()) {
      this.cache.delete(key);
      return null;
    }
    return entry.data;
  }

  remove(key) {
    this.cache.delete(key);
  }

  clear() {
    this.cache.clear();
  }

  // Deduplicate concurrent requests
  async getOrFetch(key, fetchFn, ttl = 5 * 60 * 1000) {
    // Return cached response
    const cached = this.get(key);
    if (cached) return cached;

    // Deduplicate concurrent requests
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key);
    }

    // Fetch and cache
    const promise = fetchFn()
      .then(data => {
        this.set(key, data, ttl);
        return data;
      })
      .finally(() => {
        this.pendingRequests.delete(key);
      });

    this.pendingRequests.set(key, promise);
    return promise;
  }

  dispose() {
    clearInterval(this.cleanupInterval);
    this.cache.clear();
    this.pendingRequests.clear();
  }
}

// Singleton instance
export const cacheService = new CacheService();
